// Singleton in-memory cache for catalog price maps.
// Lives for the browser session — avoids re-fetching the same region/type combo.

type PriceMap = Record<string, number>;

const cache = new Map<string, PriceMap>();
const inflight = new Map<string, Promise<PriceMap>>();

async function load(type: string, region: string): Promise<PriceMap> {
  const key = `${type}:${region}`;
  const cached = cache.get(key);
  if (cached) return cached;

  let req = inflight.get(key);
  if (!req) {
    req = fetch(`/api/catalog-prices?type=${type}&r=${region}`)
      .then(r => r.json() as Promise<PriceMap>)
      .then(data => { cache.set(key, data); inflight.delete(key); return data; })
      .catch(() => { inflight.delete(key); return {}; });
    inflight.set(key, req);
  }
  return req;
}

/** Fetch prices for given type+region. Returns {} immediately if region='any'. */
export async function fetchPrices(type: string, region: string): Promise<PriceMap> {
  if (region === 'any') return {};
  return load(type, region);
}

/** Prefetch all 4 types for a given region (call on dropdown open). */
export function prefetchRegion(region: string) {
  if (region === 'any') return;
  for (const type of ['key', 'gift', 'account', 'rental']) {
    load(type, region);
  }
}

/** Prefetch all regions for a given type. */
export function prefetchType(type: string, regions: string[]) {
  for (const r of regions) {
    if (r !== 'any') load(type, r);
  }
}
